import './styles.scss';

import { MessageOutlined, RightOutlined } from '@ant-design/icons';
import { useList, usePermissions } from '@refinedev/core';
import { Button, Empty, Skeleton } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { isTenantAdmin } from '../../accessControlProvider';
import { ApplicationItem } from '../../components/marketplace/application-item';
import { CustomSearch } from '../../components/modules/custom-search';
import { PERMISSIONS } from '../../constants/permission';
import { IMarketplace } from '../../interfaces/marketplace';

const APPLICATION_RESOURCE_URL = 'v1/application';
const SKELETON_CARDS = 8;
const SMS_BILLING_APP_CODES = ['SMS_NUMBER', 'SMS_A2P_CAMPAIGN', 'SMS_A2P_REGISTRATION'];

export const MarketplaceIndex: React.FC = () => {
    const { t } = useTranslation(['common', 'marketplace']);
    const navigate = useNavigate();
    const [filter, setFilter] = useState<string>('');
    const { data: userRights } = usePermissions<string[]>();
    const rights = userRights || [];
    // SMS billing apps are price holders billed by the SMS flows, never subscribed here: only marketplace editors see them.
    const canEdit = rights.includes(PERMISSIONS.MARKETPLACE_EDIT);

    const { data: listApplications, isLoading } = useList<IMarketplace.IApplication>({
        resource: `${APPLICATION_RESOURCE_URL}`,
        config: {
            hasPagination: false,
            // Unchanged since the marketplace launched: apps are listed whatever their publish flag.
            filters: [
                { field: 'filter', operator: 'eq', value: filter },
                { field: 'includeUnpublish', operator: 'eq', value: true },
            ],
        },
        queryOptions: { keepPreviousData: true },
    });
    const applications = (listApplications?.data ?? []).filter(
        (app) => canEdit || !SMS_BILLING_APP_CODES.includes(app.code ?? ''),
    );
    const firstLoad = isLoading && !listApplications;

    return (
        <section className="application-list-container" aria-busy={isLoading}>
            <div className="list-header">
                <div>
                    <p className="list-subtitle">
                        {t('marketplace.subtitle', { ns: 'marketplace' })}
                    </p>
                </div>
                <div className="list-search">
                    <CustomSearch
                        placeholder={t('marketplace.searchPlaceholder', { ns: 'marketplace' })}
                        aria-label={t('marketplace.searchPlaceholder', { ns: 'marketplace' })}
                        className="search-item"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setFilter(event.target.value)
                        }
                        allowClear={true}
                    />
                    {!firstLoad && (
                        <span className="list-count" aria-live="polite">
                            {t('marketplace.resultCount', {
                                ns: 'marketplace',
                                count: applications.length,
                            })}
                        </span>
                    )}
                </div>
            </div>

            {isTenantAdmin && !filter && (
                // Add-on for every tenant, not an app: a banner above the grid instead of a mismatched card inside it.
                <div className="sms-credits-banner">
                    <span className="sms-credits-banner-icon" aria-hidden="true">
                        <MessageOutlined />
                    </span>
                    <div className="sms-credits-banner-text">
                        <h2>{t('smsCredits.tile.title', { ns: 'marketplace' })}</h2>
                        <p>{t('smsCredits.tile.description', { ns: 'marketplace' })}</p>
                    </div>
                    <Button type="primary" onClick={() => navigate('/marketplace/sms-credits')}>
                        {t('smsCredits.tile.cta', { ns: 'marketplace' })}
                        <RightOutlined />
                    </Button>
                </div>
            )}

            <div className="list-application-grid">
                {firstLoad
                    ? Array.from({ length: SKELETON_CARDS }, (_, i) => (
                          <div key={i} className="application-item-page-list">
                              <Skeleton avatar active paragraph={{ rows: 2 }} />
                          </div>
                      ))
                    : applications.map((application) => (
                          <ApplicationItem
                              key={application.id}
                              application={application}
                              permission={rights}
                          />
                      ))}
            </div>

            {!firstLoad && !applications.length && (
                <Empty
                    className="list-empty"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        filter
                            ? t('marketplace.noResults', { ns: 'marketplace', query: filter })
                            : t('marketplace.noApps', { ns: 'marketplace' })
                    }
                />
            )}
        </section>
    );
};
